import { useAtom } from "jotai";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import { useEffect, useState } from "react";
import Button from "components/base/Button";
import { user_info } from "jotai/UserJotaiInstance";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faKey, faUser, faBolt, faShieldHalved, faRocket } from "@fortawesome/free-solid-svg-icons";

type Inputs = {
  email: string;
  password: string;
};

const SignInForm = ({ layout }: { layout: "simple" | "card" | "split" }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookie, setCookie, removeCookie] = useCookies([
    "isLoggedInKIQ",
    "stripeIdpKey",
  ]);
  const [status, setStatus] = useState("idle");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userInfo] = useAtom(user_info);
  const [showPassword, setShowPassword] = useState(false)
  const [pendingBrokerage, setPendingBrokerage] = useState<any>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  useEffect(() => {
    // Remove the cookie when the login page loads
    removeCookie("stripeIdpKey", { path: "/" });
    
    // Check for pending brokerage link
    const pendingLink = localStorage.getItem('pendingBrokerageLink');
    if (pendingLink) {
      setPendingBrokerage(JSON.parse(pendingLink));
    }
    
    // Check if email was passed from signup redirect
    if (location.state?.email) {
      setValue('email', location.state.email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setStatus("loading");
      const response = await axiosInstance.post("/auth/login", {
        email: data.email,
        password: data.password,
      });
      
      // 🔥 FIX: Store authentication token in localStorage
      if (response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
      }
      
      // Also check for accessToken format (enhanced auth)
      if (response.data.data?.accessToken) {
        localStorage.setItem('auth_token', response.data.data.accessToken);
      }
      
      // 🔥 FIX: Store user info in localStorage for API client
      if (response.data.data?.user) {
        localStorage.setItem('user_info', JSON.stringify(response.data.data.user));
        console.log('✅ [LOGIN] User info stored successfully');
      }
      
      setCookie("isLoggedInKIQ", true);
      
      // Handle pending brokerage link
      if (pendingBrokerage) {
        try {
          // Link user to brokerage
          await axiosInstance.post("/broker/invitations/link-existing-user", {
            email: data.email,
            brokerageName: pendingBrokerage.brokerage
          });
          
          toast.success(`Successfully joined ${pendingBrokerage.brokerage}!`);
          
          // Clear pending link
          localStorage.removeItem('pendingBrokerageLink');
        } catch (linkError) {
          console.error("Failed to link to brokerage:", linkError);
          // Still proceed with login even if linking fails
        }
      }
      
      reset();
      navigate("/dashboard");
      setStatus("idle");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.response?.data);
      setStatus("idle");
    }
  };

  return (
    <>
      {/* AI-Enhanced Header */}
      <div className="text-center mb-5">
        <div className="d-flex align-items-center justify-content-center mb-3">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ 
              width: "40px", 
              height: "40px",
              background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)',
              animation: 'ai-glow-pulse 2s infinite ease-in-out',
              willChange: 'transform'
            }}
          >
            <FontAwesomeIcon 
              icon={faBolt} 
              className="text-white" 
              size="lg"
              style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
            />
          </div>
          <h3 
            className="mb-0 fw-bold"
            style={{ 
              color: '#1f2937',
              textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
              fontSize: '1.8rem'
            }}
          >
            Sign In
          </h3>
        </div>
        
        <p 
          className="mb-2"
          style={{ 
            color: '#6b7280',
            fontSize: '1rem',
            fontWeight: '400'
          }}
        >
          Access your AI-powered dashboard
        </p>
        
        {/* AI Status Indicator */}
        <div 
          className="d-flex align-items-center justify-content-center"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            padding: '6px 12px',
            borderRadius: '15px',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            display: 'inline-flex !important',
            width: "75%",
            margin: "0 auto"
          }}
        >
          <div 
            className="rounded-circle me-2"
            style={{
              width: '6px',
              height: '6px',
              background: '#10b981',
              animation: 'ai-pulse 2s infinite'
            }}
          />
          <span 
            className="fw-semibold text-success"
            style={{ 
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Secure Connection
          </span>
        </div>
      </div>

      {/* Pending Brokerage Link Notification */}
      {pendingBrokerage && (
        <div 
          className="alert alert-info mb-4" 
          style={{ 
            background: 'rgba(0, 212, 255, 0.1)', 
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            padding: '12px 20px'
          }}
        >
          <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
          Sign in to join <strong>{pendingBrokerage.brokerage}</strong>
          {pendingBrokerage.invitedBy && (
            <span className="d-block mt-1 small">
              Invited by {pendingBrokerage.invitedBy}
            </span>
          )}
        </div>
      )}

      {/* Location State Message */}
      {location.state?.message && (
        <div 
          className="alert alert-info mb-4" 
          style={{ 
            background: 'rgba(0, 212, 255, 0.1)', 
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            padding: '12px 20px'
          }}
        >
          {location.state.message}
        </div>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-4 text-start">
          <Form.Label 
            htmlFor="email"
            className="fw-semibold mb-2"
            style={{ 
              color: '#374151',
              fontSize: '0.9rem',
              letterSpacing: '0.3px'
            }}
          >
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" size="sm" />
            Email Address
          </Form.Label>
          <div 
            className="position-relative"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden'
            }}
          >
            <Form.Control
              id="email"
              type="email"
              placeholder="Enter your email"
              autoComplete="user-email"
              {...register("email", { required: true, maxLength: 80 })}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '12px 16px',
                fontSize: '0.95rem',
                color: '#1f2937',
                boxShadow: 'none'
              }}
              className="border-0"
            />
            <div 
              className="position-absolute end-0 top-50 translate-middle-y me-3"
              style={{
                width: '24px',
                height: '24px',
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="text-white"
                size="xs"
              />
            </div>
          </div>
          {errors.email?.type === "required" && (
            <div 
              className="d-flex align-items-center mt-2"
              style={{ color: '#ef4444', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faShieldHalved} className="me-1" size="xs" />
              Email is required
            </div>
          )}
        </Form.Group>

        {/* AI-Enhanced Password Field */}
        <Form.Group className="mb-4 text-start">
          <Form.Label 
            htmlFor="password"
            className="fw-semibold mb-2"
            style={{ 
              color: '#374151',
              fontSize: '0.9rem',
              letterSpacing: '0.3px'
            }}
          >
            <FontAwesomeIcon icon={faKey} className="me-2 text-primary" size="sm" />
            Password
          </Form.Label>
          <div 
            className="position-relative"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden'
            }}
          >
            <Form.Control
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              autoComplete="current-password"
              {...register("password", {
                required: true,
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long.",
                },
                maxLength: {
                  value: 40,
                  message: "Password cannot exceed 40 characters.",
                },
              })}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '12px 50px 12px 16px',
                fontSize: '0.95rem',
                color: '#1f2937',
                boxShadow: 'none'
              }}
              className="border-0"
            />
            <div className="position-absolute end-0 top-50 translate-middle-y me-2 d-flex gap-1">
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="btn btn-link p-0 border-0"
                style={{ 
                  textDecoration: "none",
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(107, 114, 128, 0.1)',
                  borderRadius: '4px',
                  color: '#6b7280'
                }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} size="xs" />
              </button>
              <div 
                style={{
                  width: '24px',
                  height: '24px',
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon
                  icon={faKey}
                  className="text-white"
                  size="xs"
                />
              </div>
            </div>
          </div>
          {errors.password?.type === "required" && (
            <div 
              className="d-flex align-items-center mt-2"
              style={{ color: '#ef4444', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faShieldHalved} className="me-1" size="xs" />
              Password is required
            </div>
          )}
          {errors.password && errors.password.type !== "required" && (
            <div 
              className="d-flex align-items-center mt-2"
              style={{ color: '#ef4444', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faShieldHalved} className="me-1" size="xs" />
              {errors.password.message}
            </div>
          )}
        </Form.Group>

        {/* Enhanced Remember Me & Forgot Password */}
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <div 
              className="d-flex align-items-center"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '8px 12px',
                borderRadius: '8px',
              }}
            >
              <Form.Check type="checkbox" id="remember-me" className="mb-0">
                <Form.Check.Input
                  type="checkbox"
                  name="remember-me"
                  id="remember-me" 
                  defaultChecked
                  style={{ boxShadow: 'none !important', border: 'none !important', accentColor: '#00d4ff' }}
                />
                <Form.Check.Label 
                  htmlFor="remember-me" 
                  className="mb-0 ms-2"
                  style={{ 
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}
                >
                  Remember me
                </Form.Check.Label>
              </Form.Check>
            </div>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Link 
              to={`/forgot-password`} 
              className="text-decoration-none"
              style={{
                fontSize: '0.85rem',
                fontWeight: '500',
                color: '#00d4ff',
                textShadow: '0 0 5px rgba(0, 212, 255, 0.3)'
              }}
            >
              Forgot Password?
            </Link>
          </Col>
        </Row>

        {/* AI-Enhanced Sign In Button */}
        <div className="position-relative mb-4">
          <Button
            variant="primary"
            type="submit"
            className="w-100 border-0"
            disabled={status === "loading"}
            aria-label="Sign in button"
            style={{
              background: status === "loading" 
                ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                : 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: status === "loading" 
                ? 'none'
                : '0 8px 25px rgba(0, 212, 255, 0.3), 0 0 20px rgba(0, 212, 255, 0.2)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Button glow effect */}
            {status !== "loading" && (
              <div 
                className="position-absolute w-100 h-100 top-0 start-0"
                style={{
                  background: 'linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
                  animation: 'ai-shimmer 2s infinite'
                }}
              />
            )}
            
            <div className="d-flex align-items-center justify-content-center position-relative">
              {status === "loading" ? (
                <>
                  <Spinner animation="border" role="status" size="sm" className="me-2" />
                  <span>Neural Network Processing...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRocket} className="me-2" />
                  <span>Access AI Dashboard</span>
                </>
              )}
            </div>
          </Button>
        </div>

        {/* AI Security Badge */}
        <div 
          className="text-center p-3 rounded-3"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.1)'
          }}
        >
          <div className="d-flex align-items-center justify-content-center mb-2">
            <FontAwesomeIcon 
              icon={faShieldHalved} 
              className="text-success me-2" 
              size="sm"
            />
            <span 
              className="fw-semibold"
              style={{ 
                fontSize: '0.8rem',
                color: '#10b981'
              }}
            >
              Enterprise-Grade Security
            </span>
          </div>
          <small 
            style={{ 
              color: '#6b7280',
              fontSize: '0.75rem',
              lineHeight: '1.4'
            }}
          >
            Your data is protected by industry-standard encryption and secure authentication protocols
          </small>
        </div>
      </Form>

      {/* AI Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes ai-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes ai-glow-pulse {
            0%, 100% { 
              transform: scale(1);
            }
            50% { 
              transform: scale(1.05);
            }
          }
          
          @keyframes ai-shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `
      }} />
    </>
  );
};

export default SignInForm;
